export type PublisherFieldType =
  | 'text'
  | 'textarea'
  | 'select'
  | 'url'
  | 'date'
  | 'datetime-local'
  | 'check'
  | 'tags'
  | 'file';

export interface PublisherFieldDef {
  name: string;
  label: string;
  type: PublisherFieldType;
  required?: boolean;
  placeholder?: string;
  hint?: string;
  options?: Array<string | [string, string]>;
  accept?: string;
  multiple?: boolean;
}

export interface PublisherGroupDef {
  title: string;
  fields: PublisherFieldDef[];
}

const field = (
  name: string,
  label: string,
  type: PublisherFieldType = 'text',
  extra: Partial<PublisherFieldDef> = {}
): PublisherFieldDef => ({ name, label, type, ...extra });

/** ACADEMIA feed — each kind is its own field set from Publisher 1.81. */
export const ACADEMIA_GROUPS: Record<string, PublisherGroupDef[]> = {
  NEWS: [
    {
      title: 'Заголовок и краткое описание',
      fields: [
        field('title', 'Заголовок', 'text', { required: true, placeholder: 'Короткий понятный заголовок' }),
        field('summary', 'Краткое описание', 'textarea', { required: true, placeholder: '2–6 предложений' }),
      ],
    },
    {
      title: 'Категория и текст',
      fields: [
        field('category', 'Категория', 'select', {
          required: true,
          options: [
            ['portal', 'События платформы'],
            ['science', 'Новости науки'],
            ['media', 'СМИ о проекте'],
            ['updates', 'Обновления системы'],
          ],
        }),
        field('body', 'Полный текст', 'textarea', { placeholder: 'Для короткого объявления можно оставить пустым' }),
      ],
    },
    {
      title: 'Источник',
      fields: [
        field('sourceUrl', 'Официальный URL', 'url', {
          placeholder: 'https://...',
          hint: 'Обязателен для внешней новости / СМИ.',
        }),
      ],
    },
    {
      title: 'Дата и важность',
      fields: [
        field('eventDate', 'Дата публикации / события', 'datetime-local'),
        field('important', 'Показать как важное', 'check'),
        field('promoted', 'Показать на главной', 'check'),
      ],
    },
    {
      title: 'Хэштеги',
      fields: [field('hashtags', 'Хэштеги', 'tags', { required: true })],
    },
    {
      title: 'Обложка и вложения',
      fields: [
        field('cover', 'Обложка', 'file', { accept: 'image/png,image/jpeg,image/webp' }),
        field('attachments', 'Вложения', 'file', { multiple: true }),
      ],
    },
    {
      title: 'Аудитория',
      fields: [
        field('audienceMode', 'Аудитория', 'select', {
          options: [
            ['ALL', 'Все пользователи'],
            ['TARGETED', 'Выбранная аудитория'],
          ],
        }),
        field('language', 'Язык контента', 'select', {
          options: ['RU', 'KZ', 'EN', 'Мультиязычный'],
        }),
      ],
    },
  ],
  QUICK_POLL: [
    {
      title: 'Вопрос',
      fields: [
        field('title', 'Вопрос', 'text', { required: true }),
        field('summary', 'Пояснение', 'textarea'),
      ],
    },
    {
      title: 'Варианты ответа',
      fields: [
        field('pollOptions', 'Варианты — по одному в строке', 'textarea', {
          required: true,
          placeholder: 'Вариант 1\nВариант 2',
        }),
      ],
    },
    {
      title: 'Режим голосования',
      fields: [
        field('multi', 'Разрешить несколько вариантов', 'check'),
        field('reveal', 'Показывать результат до голосования', 'check'),
      ],
    },
    {
      title: 'Дата закрытия',
      fields: [field('deadline', 'Закрыть голосование', 'datetime-local')],
    },
    {
      title: 'Хэштеги',
      fields: [field('hashtags', 'Хэштеги', 'tags', { required: true })],
    },
    {
      title: 'Аудитория',
      fields: [
        field('audienceMode', 'Аудитория', 'select', {
          options: [
            ['ALL', 'Все пользователи'],
            ['TARGETED', 'Выбранная аудитория'],
          ],
        }),
        field('language', 'Язык', 'select', { options: ['RU', 'KZ', 'EN'] }),
      ],
    },
  ],
  ACHIEVEMENT: [
    {
      title: 'Тип достижения / опыта',
      fields: [
        field('subtype', 'Тип', 'select', {
          required: true,
          options: [
            'Достижение',
            'Публикация',
            'Стажировка / практика',
            'Награда / сертификат',
            'Научный опыт',
            'Методическая заметка',
            'Академическая рекомендация',
            'Другое',
          ],
        }),
      ],
    },
    {
      title: 'Заголовок и описание',
      fields: [
        field('title', 'Заголовок', 'text', { required: true }),
        field('summary', 'Краткое описание', 'textarea', { required: true }),
      ],
    },
    {
      title: 'Дата и связанный объект',
      fields: [
        field('eventDate', 'Дата достижения / опыта', 'date'),
        field('linkedObject', 'Связанный объект', 'text', {
          placeholder: 'publication / project / doctoral stage / event',
        }),
      ],
    },
    {
      title: 'Подтверждение',
      fields: [
        field('sourceUrl', 'Ссылка / документ', 'url', { placeholder: 'https://...' }),
        field('attachments', 'Файлы', 'file', { multiple: true }),
      ],
    },
    {
      title: 'Хэштеги',
      fields: [field('hashtags', 'Хэштеги', 'tags', { required: true })],
    },
    {
      title: 'Дополнительное действие',
      fields: [
        field('socialAlso', 'Также передать этот материал ответственному за соцсети / сайт ENU', 'check'),
      ],
    },
  ],
  EVENT: [
    {
      title: 'Тип мероприятия',
      fields: [
        field('subtype', 'Тип', 'select', {
          required: true,
          options: [
            'Семинар',
            'Конференция',
            'Вебинар',
            'Круглый стол',
            'Научная школа',
            'Встреча',
            'Лекция / мастер-класс',
            'Другое',
          ],
        }),
      ],
    },
    {
      title: 'Название и описание',
      fields: [
        field('title', 'Название', 'text', { required: true }),
        field('summary', 'Краткое описание', 'textarea', { required: true }),
      ],
    },
    {
      title: 'Дата и время',
      fields: [
        field('eventDate', 'Начало', 'datetime-local', { required: true }),
        field('endDate', 'Конец', 'datetime-local'),
      ],
    },
    {
      title: 'Формат и место',
      fields: [
        field('format', 'Формат', 'select', { options: ['Очно', 'Онлайн', 'Гибрид'] }),
        field('location', 'Адрес / платформа', 'text'),
      ],
    },
    {
      title: 'Организатор и источник',
      fields: [
        field('organization', 'Организатор', 'text', { required: true }),
        field('sourceUrl', 'Официальная ссылка', 'url', { placeholder: 'https://...' }),
        field('attachments', 'Файлы', 'file', { multiple: true }),
      ],
    },
    {
      title: 'Хэштеги',
      fields: [field('hashtags', 'Хэштеги', 'tags', { required: true })],
    },
  ],
};

/** Public catalog — separate from the ACADEMIA feed form. */
export const CATALOG_GROUPS: Record<string, PublisherGroupDef[]> = {
  COMPETITION: [
    {
      title: 'Название и организатор',
      fields: [
        field('title', 'Название конкурса', 'text', { required: true }),
        field('organization', 'Организатор', 'text', { required: true }),
      ],
    },
    {
      title: 'Направление и вид',
      fields: [
        field('category', 'Направление', 'select', {
          required: true,
          options: [
            ['science', 'Образование и наука'],
            ['culture', 'Культура и искусство'],
            ['innovation', 'Общество и инновации'],
            ['media', 'СМИ'],
            ['sport', 'Спорт'],
            ['other', 'Другое'],
          ],
        }),
        field('subtype', 'Вид', 'select', {
          required: true,
          options: [
            'Олимпиада / тест',
            'Научный проект',
            'Конференция / форум',
            'Искусство',
            'Литература',
            'Медиа',
            'Спорт',
            'Премия / номинация',
            'Другое',
          ],
        }),
        field('level', 'Уровень', 'select', {
          options: ['Районный', 'Городской', 'Областной', 'Республиканский', 'Международный', 'Не указано'],
        }),
      ],
    },
    {
      title: 'Формат, сроки и участники',
      fields: [
        field('format', 'Формат', 'select', { options: ['Очно', 'Онлайн', 'Гибрид'] }),
        field('location', 'Страна / регион / город', 'text'),
        field('eventDate', 'Начало', 'date'),
        field('deadline', 'Дедлайн', 'date', { required: true }),
        field('participants', 'Группа участников', 'select', {
          options: ['Ученики', 'Студенты', 'Магистранты', 'Докторанты', 'Исследователи', 'Все'],
        }),
        field('ageRange', 'Возрастной диапазон', 'text'),
      ],
    },
    {
      title: 'Стоимость, описание и источник',
      fields: [
        field('costType', 'Стоимость', 'select', { options: ['Бесплатно', 'Платно', 'Не указано'] }),
        field('amount', 'Сумма', 'text'),
        field('summary', 'Краткое описание / ключевые условия', 'textarea', { required: true }),
        field('sourceUrl', 'Официальный URL', 'url', { required: true }),
        field('contact', 'E-mail / телефон', 'text'),
        field('attachments', 'Положение / вложения', 'file', { multiple: true }),
        field('hashtags', 'Хэштеги', 'tags', { required: true }),
      ],
    },
  ],
  MATERIAL: [
    {
      title: 'Название и тип',
      fields: [
        field('title', 'Название', 'text', { required: true }),
        field('organization', 'Автор / организация', 'text'),
        field('subtype', 'Тип', 'select', {
          required: true,
          options: [
            'Книга',
            'Методическое пособие',
            'Тест',
            'Материалы конференции',
            'Журнал / газета',
            'Шаблон / образец документа',
            'Учебный материал',
            'Другое',
          ],
        }),
        field('direction', 'Направление', 'text'),
        field('language', 'Язык', 'select', { options: ['KZ', 'RU', 'EN', 'Другой', 'Мультиязычный'] }),
      ],
    },
    {
      title: 'Доступ и описание',
      fields: [
        field('access', 'Доступ', 'select', { options: ['Бесплатно', 'Платно', 'По внешней ссылке'] }),
        field('amount', 'Цена', 'text'),
        field('summary', 'Описание', 'textarea', { required: true }),
        field('sourceUrl', 'Публичный File Hub / внешний URL', 'url', { required: true }),
        field('publisher', 'Издатель', 'text'),
        field('publicationMeta', 'Место / год', 'text'),
        field('contact', 'Контакт', 'text'),
        field('sourceLabel', 'Официальный источник', 'text'),
        field('attachments', 'Обложка / вложения', 'file', { multiple: true }),
        field('hashtags', 'Хэштеги', 'tags', { required: true }),
      ],
    },
  ],
  EDUCATION: [
    {
      title: 'Тип и организация',
      fields: [
        field('subtype', 'Тип', 'select', {
          required: true,
          options: [
            'Учебное заведение',
            'Грант / стипендия',
            'Стажировка',
            'Курс / мастер-класс',
            'Центр / институт',
            'Кружок / сообщество',
          ],
        }),
        field('title', 'Название', 'text', { required: true }),
        field('organization', 'Организация', 'text', { required: true }),
        field('summary', 'Описание', 'textarea', { required: true }),
        field('direction', 'Направление', 'text'),
      ],
    },
    {
      title: 'Формат, срок и источник',
      fields: [
        field('format', 'Формат', 'select', { options: ['Очно', 'Онлайн', 'Гибрид'] }),
        field('location', 'Место', 'text'),
        field('deadline', 'Дедлайн', 'date', { hint: 'Обязателен для гранта, стажировки и набора на курс.' }),
        field('costType', 'Стоимость / финансирование', 'select', {
          options: ['Бесплатно', 'Платно', 'Грант', 'Стипендия', 'Не указано'],
        }),
        field('amount', 'Сумма', 'text'),
        field('conditions', 'Краткие требования', 'textarea'),
        field('sourceUrl', 'Официальный URL', 'url', { required: true }),
        field('contact', 'E-mail', 'text'),
        field('attachments', 'Brochure / PDF', 'file', { multiple: true, accept: '.pdf,image/*' }),
        field('hashtags', 'Хэштеги', 'tags', { required: true }),
      ],
    },
  ],
  VACANCY: [
    {
      title: 'Вакансия',
      fields: [
        field('title', 'Название вакансии', 'text', { required: true }),
        field('organization', 'Организация', 'text', { required: true }),
        field('direction', 'Направление', 'select', {
          required: true,
          options: [
            ['science', 'Педагогика и наука'],
            ['it', 'IT и технологии'],
            ['language', 'Лингвистика и редактура'],
            ['public', 'Государственная служба'],
            ['other', 'Другое'],
          ],
        }),
        field('location', 'Город / страна / Remote', 'text', { required: true }),
        field('format', 'Формат', 'select', { options: ['Очно', 'Удалённо', 'Гибрид'] }),
        field('employmentType', 'Занятость', 'select', {
          options: ['Полная', 'Частичная', 'Проектная', 'Стажировка'],
        }),
        field('deadline', 'Дедлайн', 'date'),
        field('summary', 'Основные требования', 'textarea', { required: true, placeholder: '3–8 коротких пунктов' }),
        field('salary', 'Зарплата', 'text', { placeholder: 'не указывать, если её нет в источнике' }),
        field('sourceUrl', 'Официальный URL', 'url', { required: true }),
        field('contact', 'Контакт', 'text'),
        field('attachments', 'Vacancy PDF', 'file', { accept: '.pdf,application/pdf' }),
        field('hashtags', 'Хэштеги', 'tags', { required: true }),
      ],
    },
  ],
};

/** Social / ENU site handoff — not an ACADEMIA or catalog publication. */
export const SOCIAL_GROUPS: PublisherGroupDef[] = [
  {
    title: 'Тип материала',
    fields: [
      field('subtype', 'Тип', 'select', {
        required: true,
        options: [
          'Достижение',
          'Публикация',
          'Практика',
          'Стажировка',
          'Мероприятие',
          'Награда',
          'Кафедральная новость',
          'Другое',
        ],
      }),
    ],
  },
  {
    title: 'Заголовок и описание',
    fields: [
      field('title', 'Заголовок', 'text', { required: true }),
      field('summary', 'Что произошло', 'textarea', { required: true }),
    ],
  },
  {
    title: 'Участники и дата',
    fields: [
      field('participants', 'Участники', 'text', { placeholder: 'ФИО, через запятую' }),
      field('eventDate', 'Дата', 'date'),
    ],
  },
  {
    title: 'Файлы',
    fields: [
      field('attachments', 'Фото / сертификат / документ', 'file', { multiple: true }),
    ],
  },
  {
    title: 'Желаемый текст',
    fields: [field('caption', 'Черновик подписи', 'textarea')],
  },
  {
    title: 'Каналы',
    fields: [
      field('channelInstagram', 'Instagram', 'check'),
      field('channelSite', 'сайт ENU', 'check'),
      field('channelTelegram', 'Telegram', 'check'),
    ],
  },
  {
    title: 'Хэштеги',
    fields: [field('hashtags', 'Хэштеги', 'tags')],
  },
];
